// Analytics / Insights scene — monthly ring, by-habit bars, daily line chart.
// Reads from TanStack Query (habits, checkins) so anything the dashboard
// touches shows up here instantly. Monthly/yearly goals were moved to the
// Dashboard scene (under the habit grid) so the planning surface and the
// daily check-in surface share one home.

import { useMemo, useState, useEffect } from 'react';
import { Icon } from '../components/Icon.jsx';
import {
  useHabits, useCheckins,
} from '../lib/hooks.js';
import {
  MONTHS, MONTH_NAMES_FULL, ACCENTS, resolveAccentColor, daysInMonth, getToday,
} from '../lib/constants.js';
import { useTheme } from '../lib/themeStore.js';

// ─── RingChart ────────────────────────────────────────────────────────
function RingChart({ value, size = 150, thickness = 10, children }) {
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.max(0, Math.min(100, value)) / 100);

  // PAD is the breathing room the SVG needs beyond the visible ring. The
  // ring's stroke sits at radius r; without padding, its outer edge kisses
  // the SVG viewport so the feGaussianBlur below gets chopped into a hard
  // arc right where the stroke ends. PAD=28 gives stdDeviation=2.5 plenty
  // of falloff to dissolve cleanly into transparency, and also lets us
  // paint an ambient SVG-level glow OUTSIDE the drawn ring without the
  // card's invisible square clipping it. The outer <div> stays `size×size`
  // so the ring still lays out in its card slot exactly as before — the
  // SVG is absolutely positioned and offset by -PAD so its center lines up
  // with the container's center.
  const PAD = 28;
  const outer = size + PAD * 2;
  const cx = outer / 2;
  const cy = outer / 2;

  return (
    <div style={{ width: size, height: size, position: 'relative' }}>
      {/* Soft radial halo behind the ring. Box-shadow on a zero-fill circle
          is the robust primitive: true radial falloff with no color-stop
          artifacts. Two stacked shadows give a tight inner bloom plus a
          diffuse ambient glow. */}
      <div style={{
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        background: 'none',
        pointerEvents: 'none',
        boxShadow: '0 0 36px 6px var(--glow-strong), 0 0 72px 18px var(--glow-soft)',
      }}/>
      <svg
        width={outer}
        height={outer}
        viewBox={`0 0 ${outer} ${outer}`}
        style={{
          position: 'absolute',
          top: -PAD,
          left: -PAD,
          transform: 'rotate(-90deg)',
          pointerEvents: 'none',
          overflow: 'visible',   // belt-and-braces in case UA adds a clip
        }}
      >
        <defs>
          <linearGradient id="ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: 'var(--accent-deep)' }}/>
            <stop offset="50%" style={{ stopColor: 'var(--accent)' }}/>
            <stop offset="100%" style={{ stopColor: 'var(--accent-hi)' }}/>
          </linearGradient>
          {/* Wide filter region so the Gaussian blur has real room. With the
              SVG itself padded, this filter is no longer fighting a clip. */}
          <filter id="ring-softglow" x="-75%" y="-75%" width="250%" height="250%">
            <feGaussianBlur stdDeviation="2.5" result="softBlur"/>
            <feMerge>
              <feMergeNode in="softBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          {/* Secondary ambient bloom — a wider, softer pass painted behind
              the stroke for depth. Low opacity so it reads as atmosphere. */}
          <filter id="ring-ambient" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="8"/>
          </filter>
        </defs>
        {/* Ambient outer bloom — a low-opacity, heavily blurred copy of the
            progress stroke painted first so it sits behind everything. This
            is what turns a flat colored arc into something that feels like
            it glows into the surrounding card. */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke="url(#ring-grad)"
          strokeWidth={thickness}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          opacity="0.55"
          filter="url(#ring-ambient)"
          style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(0.2,0.7,0.2,1)' }}
        />
        {/* Track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={thickness}/>
        {/* Foreground progress stroke, tight glow */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke="url(#ring-grad)"
          strokeWidth={thickness}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          filter="url(#ring-softglow)"
          style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(0.2,0.7,0.2,1)' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
        {children}
      </div>
    </div>
  );
}

// ─── LineChart ────────────────────────────────────────────────────────
// Daily progress line. Three design choices worth calling out:
//
// 1. Curve smoothing. The raw series is inherently zig-zaggy (100 one day,
//    0 the next), and connecting straight segments produces hard peaks
//    that read as "failure spikes" more than "progress over time." We
//    interpolate with a Catmull-Rom → cubic Bezier spline so the curve
//    glides between points. Tension 0.2 keeps it honest — enough bend to
//    look organic without the overshoot that makes a value lie above its
//    own point.
//
// 2. X-axis labels. Each day of the month so far gets a label; we stride
//    (every day / every other / every 5) based on density so 31 days on
//    a phone-width chart don't crush into each other. Labels live in an
//    HTML strip BELOW the SVG rather than inside it — the SVG uses
//    preserveAspectRatio="none" so inline <text> would stretch along
//    with the plot, squishing numbers. Absolute-positioned HTML keeps
//    the numerals at their designed width regardless of container size.
//
// 3. Y-axis labels. Same story as X — rendered as HTML in a fixed-width
//    gutter to the left of the SVG, so they don't stretch with the plot.
//    We also reserve PADY_TOP inside the plot so the 100%-peak line (plus
//    its drop-shadow bloom) doesn't get clipped by the top of the SVG.
function LineChart({ data, h = 120 }) {
  // Active node index — the day the user most recently tapped. Null means
  // no tooltip is showing. Tapping the same node again dismisses; tapping
  // another node replaces. Tapping anywhere on the chart background also
  // dismisses so the chart doesn't feel sticky.
  const [activeIdx, setActiveIdx] = useState(null);

  // Clamp the active index if the series length changes underneath us
  // (user flips months on the tab). Prevents a stale index from pointing
  // past the end of a shorter month.
  useEffect(() => {
    if (activeIdx != null && activeIdx >= data.length) setActiveIdx(null);
  }, [data.length, activeIdx]);

  if (!data.length) return null;
  const w = 320;
  const PADX = 10;                 // horizontal inset inside the plot so end labels can center
  const PADY_TOP = 14;             // extra top breathing room so the 100% peak + glow isn't cut
  const PADY_BOTTOM = 6;           // bottom breathing room inside plot
  const Y_AXIS_W = 26;             // reserved gutter on the left for % labels
  const plotW = w - 2 * PADX;
  const max = Math.max(...data, 1);
  const step = data.length > 1 ? plotW / (data.length - 1) : plotW;
  const points = data.map((v, i) => [
    PADX + i * step,
    h - (v / max) * (h - PADY_TOP - PADY_BOTTOM) - PADY_BOTTOM,
  ]);

  // Catmull-Rom to cubic Bezier. Endpoints are duplicated so the curve
  // starts and ends with a well-defined tangent instead of flipping around.
  const tension = 0.2;
  let path = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || points[i + 1];
    const c1x = p1[0] + (p2[0] - p0[0]) * tension;
    const c1y = p1[1] + (p2[1] - p0[1]) * tension;
    const c2x = p2[0] - (p3[0] - p1[0]) * tension;
    const c2y = p2[1] - (p3[1] - p1[1]) * tension;
    path += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  const lastX = points[points.length - 1][0];
  const areaPath = `${path} L ${lastX} ${h} L ${points[0][0]} ${h} Z`;

  // Stride picker for day labels. Always include the first and last day so
  // the user can orient themselves to "day 1" and "today".
  const n = data.length;
  let stride = 1;
  if (n > 22) stride = 5;
  else if (n > 14) stride = 2;

  const labels = [];
  for (let i = 0; i < n; i++) {
    const isEdge = i === 0 || i === n - 1;
    if (isEdge || i % stride === 0) {
      labels.push({ day: i + 1, xPct: ((PADX + i * step) / w) * 100 });
    }
  }
  // If the final (always-included) label is right next to a strided label,
  // drop the strided one so two numbers don't collide visually.
  if (stride > 1 && labels.length >= 2) {
    const lastTwo = labels[labels.length - 1].day - labels[labels.length - 2].day;
    if (lastTwo === 1) labels.splice(labels.length - 2, 1);
  }

  // Y-axis tick definitions. % of the plot area from the top of the SVG.
  const yTicks = [100, 75, 50, 25];
  const yTickPct = (g) =>
    ((h - (g / 100) * (h - PADY_TOP - PADY_BOTTOM) - PADY_BOTTOM) / h) * 100;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Y-axis % gutter — fixed pixel width so numerals don't stretch */}
        <div style={{ position: 'relative', width: Y_AXIS_W, height: h, flexShrink: 0 }}>
          {yTicks.map(g => (
            <span key={g} style={{
              position: 'absolute',
              right: 6,
              top: `${yTickPct(g)}%`,
              transform: 'translateY(-50%)',
              fontFamily: 'var(--font-mono, var(--font-body))',
              fontSize: 9,
              color: 'var(--fg-4)',
              letterSpacing: '0.02em',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}>{g}%</span>
          ))}
        </div>

        {/* Plot area
            ----------------------------------------------------------
            onClick on the wrapper (not the SVG) dismisses any active
            readout when the user taps empty chart space. The tap
            targets on each node stopPropagation so they don't bubble
            up and dismiss themselves. */}
        <div
          style={{ flex: 1, minWidth: 0, position: 'relative' }}
          onClick={() => setActiveIdx(null)}
        >
          <svg
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="none"
            style={{ width: '100%', height: h, display: 'block', overflow: 'visible' }}
          >
            <defs>
              <linearGradient id="area-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: 'var(--accent)', stopOpacity: 0.35 }}/>
                <stop offset="100%" style={{ stopColor: 'var(--accent)', stopOpacity: 0 }}/>
              </linearGradient>
            </defs>
            {yTicks.map((g, i) => {
              const y = h - (g / 100) * (h - PADY_TOP - PADY_BOTTOM) - PADY_BOTTOM;
              return (
                <line key={i} x1={PADX} x2={w - PADX} y1={y} y2={y}
                      stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="2 4"/>
              );
            })}
            <path d={areaPath} fill="url(#area-grad)"/>
            <path d={path} fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ stroke: 'var(--accent)', filter: 'drop-shadow(0 0 6px var(--glow-strong))' }}/>
            {/* Node markers are deliberately NOT rendered here as SVG
                <circle> elements — with preserveAspectRatio="none" the
                SVG stretches horizontally and turns every circle into a
                horizontally-squished ellipse on wide viewports. The
                lines/paths tolerate that stretch fine (they're 1D), but
                the dots looked broken. They're rendered as round HTML
                divs in the overlay below where the hit-targets already
                live — same visual position, uniform circular shape at
                every viewport width. */}
          </svg>

          {/* HTML overlay — both the visible round nodes AND the
              transparent hit-targets. Same div so the dot and the click
              region always line up regardless of viewport stretching. */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {points.map(([x, y], i) => {
              const xPct = (x / w) * 100;
              const yPct = (y / h) * 100;
              const isActive = i === activeIdx;
              const dotSize = isActive ? 9 : 4;
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`Day ${i + 1}: ${Math.round(data[i])}%`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIdx(prev => (prev === i ? null : i));
                  }}
                  style={{
                    position: 'absolute',
                    left: `${xPct}%`,
                    top: `${yPct}%`,
                    transform: 'translate(-50%, -50%)',
                    width: 28, height: 28,
                    padding: 0, margin: 0, border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    WebkitTapHighlightColor: 'transparent',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: dotSize, height: dotSize,
                      borderRadius: '50%',
                      background: isActive ? '#fff' : 'var(--accent)',
                      border: isActive ? '1.5px solid var(--accent)' : 'none',
                      boxShadow: isActive ? '0 0 8px var(--glow-strong)' : 'none',
                      transition: 'width 120ms ease, height 120ms ease, background 120ms ease',
                    }}
                  />
                </button>
              );
            })}
          </div>

          {/* Readout tooltip for the active node. Clamped so it doesn't
              slide past the left/right edge of the plot when the active
              node is near a boundary. */}
          {activeIdx != null && (() => {
            const [ax, ay] = points[activeIdx];
            const xPct = Math.max(6, Math.min(94, (ax / w) * 100));
            const yPct = (ay / h) * 100;
            return (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  left: `${xPct}%`,
                  top: `${yPct}%`,
                  transform: 'translate(-50%, calc(-100% - 12px))',
                  padding: '6px 10px',
                  borderRadius: 10,
                  background: 'rgba(12,13,15,0.92)',
                  border: '1px solid var(--stroke-1)',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.4)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 11,
                  color: 'var(--fg-1)',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'auto',
                  lineHeight: 1.25,
                }}
              >
                <div className="eyebrow" style={{ fontSize: 8.5, color: 'var(--fg-4)', marginBottom: 2 }}>
                  Day {activeIdx + 1}
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 13, color: 'var(--fg-1)', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(data[activeIdx])}%
                </div>
              </div>
            );
          })()}
          <div style={{ position: 'relative', height: 14, marginTop: 4 }}>
            {labels.map(({ day, xPct }) => (
              <span key={day} style={{
                position: 'absolute',
                left: `${xPct}%`,
                transform: 'translateX(-50%)',
                fontFamily: 'var(--font-mono, var(--font-body))',
                fontSize: 9,
                color: 'var(--fg-4)',
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}>{day}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Scene ────────────────────────────────────────────────────────────
export function Analytics({ month, year, onOpenSettings }) {
  const theme = useTheme();
  const { data: habits = [] } = useHabits();
  const { data: checkins = [] } = useCheckins();

  const today = getToday();
  const totalDays = daysInMonth(year, month);
  const isCurrentMonth = (year === today.year && month === today.month);
  const cutoff = isCurrentMonth ? today.day : totalDays;

  // Build a lookup { "habitId|YYYY-MM-DD": true } for O(1) access.
  const doneSet = useMemo(() => {
    const s = new Set();
    for (const c of checkins) {
      if (c.status === 'done') s.add(`${c.habitId}|${c.date}`);
    }
    return s;
  }, [checkins]);

  const isoFor = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  // By habit — completion % over the month up to the cutoff.
  const byHabit = useMemo(() => habits.map(h => {
    let done = 0;
    for (let d = 1; d <= cutoff; d++) {
      if (doneSet.has(`${h.id}|${isoFor(year, month, d)}`)) done++;
    }
    return {
      ...h,
      done,
      possible: cutoff,
      pct: cutoff > 0 ? Math.round((done / cutoff) * 100) : 0,
    };
  }), [habits, doneSet, cutoff, month, year]);

  // Daily progress series — % of active habits completed on each day.
  const dailySeries = useMemo(() => Array.from({ length: cutoff }, (_, i) => {
    const d = i + 1;
    if (habits.length === 0) return 0;
    const iso = isoFor(year, month, d);
    let done = 0;
    for (const h of habits) if (doneSet.has(`${h.id}|${iso}`)) done++;
    return Math.round((done / habits.length) * 100);
  }), [habits, doneSet, cutoff, month, year]);

  const totalDone = byHabit.reduce((a, h) => a + h.done, 0);
  const totalPossible = habits.length * cutoff;
  const overallPct = totalPossible > 0 ? Math.round((totalDone / totalPossible) * 100) : 0;

  const bestHabit = [...byHabit].sort((a, b) => b.pct - a.pct)[0];
  const perfectDays = dailySeries.filter(v => v === 100).length;

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      {/* Header — no hamburger button here anymore. Settings live under the
          Profile tab now, so the menu icon was a dead UI element that also
          threw off the visual centering of the title (the old layout had to
          paddingRight to fake-center past the button). */}
      <div style={{ padding: '8px 20px 10px', textAlign: 'center' }}>
        <div className="eyebrow" style={{ fontSize: 9 }}>Insights & Goals</div>
        <h1 className="title" style={{ marginTop: 6, fontSize: 22 }}>
          <span className="accent">Momentum</span> Report
        </h1>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-3)', marginTop: 4, fontStyle: 'italic' }}>
          {MONTH_NAMES_FULL[month]} {year}
        </div>
      </div>

      {/* Total monthly completion ring */}
      <div className="stat-hero">
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <RingChart value={overallPct} size={132} thickness={10}>
            <div>
              <div className="grad-text" style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 40, letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {overallPct}<span style={{ fontSize: 20 }}>%</span>
              </div>
              <div className="eyebrow" style={{ fontSize: 9, marginTop: 4, color: 'var(--fg-3)' }}>Done</div>
            </div>
          </RingChart>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow" style={{ fontSize: 9 }}>Monthly completion</div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--fg-1)', marginTop: 6, letterSpacing: '-0.01em' }}>
              {totalDone} <span style={{ color: 'var(--fg-4)', fontWeight: 500 }}>/ {totalPossible} checks</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--fg-3)' }}>
                <Icon name="trophy" size={12} color="#FFB547"/>
                <span style={{ color: 'var(--fg-1)', fontWeight: 600, fontFamily: 'var(--font-display)' }}>{perfectDays}</span>
                <span>perfect {perfectDays === 1 ? 'day' : 'days'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--fg-3)' }}>
                <Icon name="trend-up" size={12} color="#30D394"/>
                <span style={{ color: 'var(--fg-1)', fontWeight: 600, fontFamily: 'var(--font-display)' }}>{bestHabit?.pct ?? 0}%</span>
                <span>best: {bestHabit?.name ?? '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* By habit bars */}
      {habits.length > 0 && (
        <div style={{ margin: '0 20px 16px', padding: '16px 18px', borderRadius: 18, background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div className="eyebrow">By habit · {MONTHS[month]}</div>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'var(--fg-4)' }}>% complete</span>
          </div>
          <div>
            {byHabit.map(h => {
              const color = resolveAccentColor(h.accent, theme);
              return (
                <div key={h.id} className="hbar-row">
                  {/* Layout is controlled by the .hbar-label class now — the
                      inline flex/gap styles used to override the CSS and
                      re-introduce overflow:hidden in the label container,
                      which clipped the dot's glow. The class handles flex
                      layout; the text sits inside .hbar-label-text so only
                      the text (not the dot) is truncated with ellipsis. */}
                  <div className="hbar-label">
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}`, flexShrink: 0 }}/>
                    <span className="hbar-label-text">{h.name}</span>
                  </div>
                  <div className="hbar-track">
                    <div className="hbar-fill" style={{
                      width: `${h.pct}%`,
                      background: `linear-gradient(90deg, ${color}AA, ${color})`,
                      boxShadow: `0 0 8px ${color}77`,
                    }}/>
                  </div>
                  <div className="hbar-val">{h.pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Daily progress line chart */}
      {habits.length > 0 && (
        <div style={{ margin: '0 20px 16px', padding: '16px 18px', borderRadius: 18, background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="eyebrow">Daily progress</div>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'var(--fg-4)' }}>Day 1 → {cutoff}</span>
          </div>
          <div style={{ position: 'relative' }}>
            <LineChart data={dailySeries} h={110}/>
          </div>
        </div>
      )}

    </div>
  );
}
